import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ora7Image from '../assets/products/ora7.png';
import {
  FiDownload,
  FiCheck,
  FiAlertCircle,
  FiX,
  FiCheckCircle,
  FiArrowRight,
  FiShield,
  FiZap,
  FiDollarSign,
  FiTrendingUp,
  FiTool,
  FiDroplet,
  FiClock,
  FiLayers,
} from 'react-icons/fi';
import {
  BsDroplet,
  BsShieldCheck,
  BsLightning,
  BsFire,
  BsTools,
} from 'react-icons/bs';
import { GiChemicalDrop, GiOilDrum, GiDroplets, GiCrackedGlass } from 'react-icons/gi';
import InquiryModal from '../components/InquiryModal';
import ruOra7Brochure from '../assets/pdf/Pamphletruora7.pdf';
import ContactFormModal from '../components/ContactFormModal';

const ProductRUORA7 = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [downloadType, setDownloadType] = useState('');
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleDownloadClick = (type) => {
    setDownloadType(type);
    setModalOpen(true);
  };

  const industrialChallenges = [
    {
      title: 'Fire Hazard',
      desc: 'Using flammable solvents like diesel creates a significant fire risk, especially in sensitive areas like oil drilling or underground mines',
      icon: BsFire,
      color: 'from-red-500 to-orange-500',
    },
    {
      title: 'Environmental Strain',
      desc: 'Contributes to fossil fuel consumption and is harmful to the environment',
      icon: FiAlertCircle,
      color: 'from-amber-500 to-yellow-500',
    },
    {
      title: 'Time and Labor',
      desc: 'Conventional methods consume long man-hours and often leave traces of residue',
      icon: FiClock,
      color: 'from-orange-500 to-red-500',
    },
  ];

  const solutionFeatures = [
    {
      title: 'Water-Based & Safe',
      desc: 'Non-flammable, non-corrosive, non-toxic, and free of bleaching agents and CFC',
      icon: BsShieldCheck,
      gradient: 'from-green-500 to-emerald-500',
    },
    {
      title: 'Effective Replacement',
      desc: 'Replaces the need for hazardous fossil fuels in cleaning applications',
      icon: FiCheckCircle,
      gradient: 'from-blue-500 to-cyan-500',
    },
    {
      title: 'Highly Dilutable',
      desc: 'Concentrated formula can be diluted with water from 1:1 up to 1:10, making it exceptionally cost-effective for large-scale operations',
      icon: BsDroplet,
      gradient: 'from-purple-500 to-pink-500',
    },
  ];

  const threeInOneAdvantages = [
    {
      number: '1',
      title: 'Superior Oil Remover',
      desc: 'RU ORA-7 cleans any type of oil from any surface without causing damage, leaving an absolutely clean and dry surface',
      applications: ['Floors', 'Oil Pits', 'Storage Tanks', 'Heavy Equipment like Cranes and Excavators'],
      icon: GiOilDrum,
      gradient: 'from-orange-600 to-red-600',
      bgGradient: 'from-orange-50 to-red-50',
    },
    {
      number: '2',
      title: 'De-Rusting Supplement',
      desc: 'This concentrated solution reacts only on the oxidized (rust) portion. It effectively opens rusted nut-bolts and removes surface rust without damaging the integrity of the original metal',
      applications: ['Rusted Nut-Bolts', 'Surface Rust Removal', 'Metal Preservation', 'Equipment Restoration'],
      icon: GiDroplets,
      gradient: 'from-amber-600 to-orange-600',
      bgGradient: 'from-amber-50 to-orange-50',
    },
    {
      number: '3',
      title: 'Efficient Carbon Remover',
      desc: 'Despite being water-based, RU ORA-7 achieves better de-carbonizing results compared to traditional methods using fossil fuels. This provides a safer, more effective way to clean internal engine components and equipment exposed to soot',
      applications: ['Internal Engine Components', 'Soot Removal', 'Equipment Cleaning', 'Industrial Machinery'],
      icon: GiCrackedGlass,
      gradient: 'from-slate-600 to-gray-700',
      bgGradient: 'from-slate-50 to-gray-100',
    },
  ];

  const roiBenefits = [
    {
      title: 'Maximizes Safety',
      desc: 'Non-flammable composition makes it the only safe choice for offshore oil drilling and underground mines. Even helps control fire by acting as a foaming agent and tracing oil leakage',
      icon: BsShieldCheck,
      color: 'text-green-600',
      bgColor: 'from-green-500 to-emerald-500',
    },
    {
      title: 'Saves Fossil Fuels',
      desc: 'Directly eliminates the need to purchase and use diesel or kerosene for cleaning, leading to significant cost savings and reduced carbon footprint',
      icon: FiDollarSign,
      color: 'text-blue-600',
      bgColor: 'from-blue-500 to-cyan-500',
    },
    {
      title: 'Saves Labor',
      desc: 'Combines three maintenance water base solution into one product, simplifying purchasing and inventory, and reducing time spent on multiple cleaning processes',
      icon: FiTool,
      color: 'text-purple-600',
      bgColor: 'from-purple-500 to-pink-500',
    },
  ];

  // Molecular pattern
  const MolecularPattern = () => (
    <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="molecule-ora7" x="0" y="0" width="200" height="200" patternUnits="userSpaceOnUse">
          <circle cx="20" cy="20" r="3" fill="currentColor" opacity="0.3" />
          <circle cx="80" cy="40" r="4" fill="currentColor" opacity="0.2" />
          <circle cx="140" cy="70" r="2" fill="currentColor" opacity="0.4" />
          <line x1="20" y1="20" x2="80" y2="40" stroke="currentColor" strokeWidth="0.5" opacity="0.2" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#molecule-ora7)" />
    </svg>
  );

  return (
    <div className="min-h-screen overflow-hidden bg-white">
      {/* Section 1: Hero Banner & Core Promise */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-32">
        {/* Dynamic Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100">
          <div 
            className="absolute inset-0 opacity-30"
            style={{
              background: `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(251, 146, 60, 0.2), transparent 50%)`,
            }}
          />
        </div>

        {/* Molecular Pattern */}
        <div className="absolute inset-0 text-orange-400 opacity-20">
          <MolecularPattern />
        </div>

        {/* Floating industrial elements */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(12)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -60, 0],
                x: [0, Math.random() * 40 - 20, 0],
                scale: [1, 1.3, 1],
                opacity: [0.3, 0.6, 0.3],
              }}
              transition={{
                duration: 6 + Math.random() * 3,
                repeat: Infinity,
                delay: Math.random() * 3,
              }}
            >
              <GiOilDrum className="w-10 h-10 text-orange-400/50" />
            </motion.div>
          ))}
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 lg:px-8">
          <motion.div
            className="max-w-6xl mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Badge */}
            <motion.div
              className="inline-flex items-center space-x-3 bg-gradient-to-r from-orange-600/10 to-red-600/10 backdrop-blur-xl px-6 py-3 rounded-full text-slate-900 mb-8 border border-orange-600/30"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <GiChemicalDrop className="text-orange-700 text-2xl animate-pulse" />
              <span className="font-semibold">3-in-1 Industrial Solution</span>
            </motion.div>

            {/* Main Headline */}
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-black text-slate-900 mb-6 leading-tight">
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-700 via-red-700 to-orange-700">
                RU ORA-7
              </span>
              <span className="block text-3xl md:text-4xl lg:text-5xl mt-4 text-slate-900 font-bold">
                The Non-Flammable,
              </span>
              <span className="block text-4xl md:text-5xl lg:text-6xl text-orange-700 font-bold">
                3-in-1 Powerhouse
              </span>
              <span className="block text-3xl md:text-4xl lg:text-5xl mt-2 text-slate-900 font-bold">
                for Industrial Maintenance
              </span>
            </h1>

            {/* Sub-headline */}
            <motion.p
              className="text-xl md:text-2xl lg:text-3xl text-slate-800 mb-12 max-w-5xl mx-auto leading-relaxed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              Safely tackle{' '}
              <span className="text-orange-700 font-bold">oil, rust, and carbon buildup</span> using a single,
              biodegradable, water-based solution—replacing hazardous traditional operations.
            </motion.p>

            {/* CTAs */}
            <motion.div
              className="flex flex-col sm:flex-row items-center justify-center gap-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <button
                onClick={() => setContactModalOpen(true)}
                className="group px-12 py-6 bg-gradient-to-r from-orange-600 to-red-600 text-white font-bold text-xl rounded-full hover:shadow-2xl hover:shadow-orange-500/50 transform hover:scale-105 transition-all duration-300 flex items-center space-x-3"
              >
                <span>Request a Quote</span>
                <FiArrowRight className="group-hover:translate-x-2 transition-transform" />
              </button>

              <button
                onClick={() => handleDownloadClick('brochure')}
                className="group px-12 py-6 bg-white/90 backdrop-blur-md text-slate-900 font-bold text-xl rounded-full border-2 border-orange-300 hover:bg-white hover:text-orange-900 transition-all duration-300 flex items-center space-x-3"
              >
                <FiDownload className="text-2xl" />
                <span>Download Brochure</span>
              </button>
            </motion.div>

            {/* Trust indicators */}
            <motion.div
              className="mt-16 flex flex-wrap items-center justify-center gap-8 text-sm text-slate-900 font-semibold"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
            >
              <div className="flex items-center space-x-2">
                <FiCheckCircle className="text-orange-400" />
                <span>Non-Flammable</span>
              </div>
              <div className="flex items-center space-x-2">
                <FiCheckCircle className="text-orange-400" />
                <span>Biodegradable</span>
              </div>
              <div className="flex items-center space-x-2">
                <FiCheckCircle className="text-orange-400" />
                <span>Water-Based</span>
              </div>
              <div className="flex items-center space-x-2">
                <FiCheckCircle className="text-orange-400" />
                <span>3-in-1 Formula</span>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-8 h-14 border-2 border-slate-900 rounded-full flex justify-center p-2">
            <motion.div 
              className="w-2 h-4 bg-gradient-to-b from-orange-400 to-red-400 rounded-full"
              animate={{ y: [0, 20, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </div>
        </motion.div>
      </section>

      {/* Section 2: The Industrial Challenge */}
      <section id="challenge" className="py-24 md:py-32 bg-gradient-to-br from-red-50 via-orange-50 to-amber-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 right-0 w-96 h-96 bg-red-200 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-200 rounded-full blur-3xl" />
        </div>

        <div className="relative container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {/* Section Header */}
            <div className="text-center mb-16">
              <motion.div
                className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-6 border-4 border-red-200"
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <FiAlertCircle className="w-10 h-10 text-red-600" />
              </motion.div>

              <h2 className="text-5xl md:text-6xl font-black text-slate-900 mb-6">
                The{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-orange-600">
                  Industrial Challenge
                </span>
              </h2>
              
              <p className="text-xl md:text-2xl text-slate-900 max-w-5xl mx-auto leading-relaxed">
                Heavy industrial environments—from{' '}
                <span className="font-bold text-orange-700">offshore rigs to manufacturing floors</span>—face constant
                challenges from oil spillage, rust formation, and carbon soot buildup. Historically, maintenance teams
                relied on fossil fuels like diesel or kerosene for effective cleaning. This practice is fraught with
                risks and inefficiencies:
              </p>
            </div>

            {/* Challenge Cards */}
            <div className="grid md:grid-cols-3 gap-8 mb-16 max-w-6xl mx-auto">
              {industrialChallenges.map((challenge, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15 }}
                  whileHover={{ y: -10, scale: 1.03 }}
                  className="group relative"
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${challenge.color} opacity-10 group-hover:opacity-20 blur-xl transition-opacity duration-500 rounded-3xl`} />
                  
                  <div className="relative bg-white rounded-3xl p-8 shadow-xl border-2 border-red-200 hover:border-red-400 transition-all duration-300">
                    <motion.div
                      className={`w-16 h-16 bg-gradient-to-br ${challenge.color} rounded-2xl flex items-center justify-center mb-6`}
                      whileHover={{ rotate: 360, scale: 1.1 }}
                      transition={{ duration: 0.6 }}
                    >
                      <challenge.icon className="w-8 h-8 text-white" />
                    </motion.div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">{challenge.title}</h3>
                    <p className="text-slate-900 leading-relaxed text-lg">{challenge.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* CTA */}
            <motion.div
              className="text-center"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <a
                href="#solution"
                className="inline-flex items-center space-x-3 px-10 py-5 bg-gradient-to-r from-red-600 to-orange-600 text-white font-bold text-lg rounded-full hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
              >
                <BsFire className="text-2xl" />
                <span>Eliminate Fire Hazards—Switch to RU ORA-7 Today</span>
                <FiArrowRight className="text-2xl" />
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Section 3: The VP Chem Technologies Solution */}
      <section id="solution" className="py-24 md:py-32 bg-gradient-to-br from-orange-50 via-red-50 to-orange-50 relative overflow-hidden">
        {/* Animated grid */}
        <div className="absolute inset-0 opacity-5">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(to right, rgba(251, 146, 60, 0.3) 1px, transparent 1px),
                                linear-gradient(to bottom, rgba(251, 146, 60, 0.3) 1px, transparent 1px)`,
              backgroundSize: '50px 50px',
            }}
          />
        </div>

        {/* Floating molecules */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -50, 0],
                opacity: [0.1, 0.3, 0.1],
              }}
              transition={{
                duration: 8 + Math.random() * 4,
                repeat: Infinity,
                delay: Math.random() * 5,
              }}
            >
              <div className="w-3 h-3 bg-orange-400 rounded-full" />
            </motion.div>
          ))}
        </div>

        <div className="relative container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {/* Section Header */}
            <div className="text-center mb-16">
              <motion.span
                className="inline-block px-6 py-2 bg-orange-600/10 backdrop-blur-xl rounded-full text-orange-700 font-semibold mb-6 border border-orange-600/30"
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ type: "spring" }}
              >
                🧪 The VP Chem Technologies Solution
              </motion.span>
              
              <h2 className="text-5xl md:text-6xl lg:text-7xl font-black mb-8">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">
                  RU ORA-7
                </span>
                <br />
                <span className="text-vp-grey-800 text-4xl md:text-5xl mt-4 block">
                  Eco-Friendly, Bio-Degradable & Water-Based
                </span>
              </h2>
              
              <p className="text-xl md:text-2xl text-vp-grey-800 max-w-4xl mx-auto leading-relaxed">
                RU ORA-7 is specifically formulated as a concentrated{' '}
                <span className="text-vp-green-700 font-bold">3-in-1 organic water base solution</span>. Developed to solve
                core industrial maintenance headaches, RU ORA-7 provides superior results safely.
              </p>
            </div>

            {/* Product Image */}
            <motion.div
              className="max-w-4xl mx-auto mb-16"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden border-4 border-orange-200">
                <img 
                  src={ora7Image} 
                  alt="RU ORA-7 Product" 
                  className="w-full h-auto object-cover"
                />
              </div>
            </motion.div>

            {/* Solution Features */}
            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">
              {solutionFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15 }}
                  whileHover={{ y: -10 }}
                  className="group relative"
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500 rounded-3xl`} />
                  
                  <div className="relative bg-white backdrop-blur-xl border-2 border-orange-200 rounded-3xl p-8 h-full group-hover:bg-orange-50 group-hover:border-orange-400 transition-all duration-500 shadow-lg">
                    <motion.div
                      className="w-20 h-20 bg-orange-100 backdrop-blur-xl rounded-2xl flex items-center justify-center mb-6"
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.6 }}
                    >
                      <feature.icon className="w-10 h-10 text-orange-600" />
                    </motion.div>
                    
                    <h3 className="text-2xl md:text-3xl font-bold text-vp-grey-900 mb-4">
                      {feature.title}
                    </h3>
                    <p className="text-vp-grey-800 leading-relaxed text-lg">
                      {feature.desc}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* CTA */}
            <motion.div
              className="text-center"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <button
                onClick={() => handleDownloadClick('technical-brochure')}
                className="group inline-flex items-center space-x-3 px-10 py-5 bg-white text-orange-900 font-bold text-lg rounded-full hover:bg-orange-100 transform hover:scale-105 transition-all duration-300 shadow-2xl"
              >
                <FiDownload className="text-2xl" />
                <span>Download RU ORA-7 Technical Brochure</span>
                <FiArrowRight className="group-hover:translate-x-2 transition-transform" />
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Section 4: The 3-in-1 Advantage */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-orange-50 via-amber-50 to-red-50 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-1/2 h-1/2 bg-gradient-to-br from-orange-100/50 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-1/2 h-1/2 bg-gradient-to-br from-red-100/50 to-transparent rounded-full blur-3xl" />
        
        <div className="relative container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {/* Section Header */}
            <div className="text-center mb-16">
              <motion.span
                className="inline-block px-6 py-2 bg-gradient-to-r from-orange-100 to-red-100 rounded-full text-orange-700 font-semibold mb-6"
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ type: "spring" }}
              >
                🔧 Triple-Action Formula
              </motion.span>
              
              <h2 className="text-5xl md:text-6xl font-black text-slate-900 mb-6">
                The{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">
                  3-in-1 Advantage
                </span>
              </h2>
              
              <p className="text-xl md:text-2xl text-slate-900 max-w-4xl mx-auto leading-relaxed">
                One powerful solution that replaces three separate maintenance water base solution
              </p>
            </div>

            {/* Three Advantages */}
            <div className="max-w-7xl mx-auto space-y-12">
              {threeInOneAdvantages.map((advantage, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 items-center`}
                >
                  {/* Icon Side */}
                  <motion.div
                    className="flex-shrink-0"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                  >
                    <div className={`w-48 h-48 bg-gradient-to-br ${advantage.gradient} rounded-3xl flex items-center justify-center shadow-2xl`}>
                      <advantage.icon className="w-24 h-24 text-white" />
                    </div>
                  </motion.div>

                  {/* Content Side */}
                  <div className={`flex-1 bg-gradient-to-br ${advantage.bgGradient} rounded-3xl p-10 shadow-xl border-2 border-orange-200`}>
                    <div className="flex items-center space-x-4 mb-4">
                      <div className={`w-14 h-14 bg-gradient-to-br ${advantage.gradient} rounded-2xl flex items-center justify-center text-white text-2xl font-black`}>
                        {advantage.number}
                      </div>
                      <h3 className="text-3xl md:text-4xl font-black text-slate-900">
                        {advantage.title}
                      </h3>
                    </div>

                    <p className="text-lg md:text-xl text-slate-900 leading-relaxed mb-6">
                      {advantage.desc}
                    </p>

                    {/* Applications */}
                    <div className="space-y-3">
                      <h4 className="text-lg font-bold text-slate-900">Applications:</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {advantage.applications.map((app, appIndex) => (
                          <motion.div
                            key={appIndex}
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: appIndex * 0.1 }}
                            className="flex items-center space-x-2 bg-white/70 backdrop-blur-sm px-4 py-3 rounded-xl"
                          >
                            <FiCheck className="text-orange-700 flex-shrink-0 text-xl" />
                            <span className="text-slate-900 font-medium">{app}</span>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section 5: Unrivaled Safety and ROI */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-orange-50 via-red-50 to-orange-50 relative overflow-hidden">
        {/* Animated blobs */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl animate-blob" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-red-500/10 rounded-full blur-3xl animate-blob" style={{ animationDelay: '2s' }} />
        </div>

        <div className="relative container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {/* Section Header */}
            <div className="text-center mb-16">
              <motion.span
                className="inline-block px-6 py-2 bg-orange-600/10 backdrop-blur-xl rounded-full text-orange-700 font-semibold mb-6 border border-orange-600/30"
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ type: "spring" }}
              >
                💰 Unrivaled Safety and ROI
              </motion.span>
              
              <h2 className="text-5xl md:text-6xl lg:text-7xl font-black mb-8 text-vp-grey-900">
                The Compelling{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">
                  Business Case
                </span>
              </h2>
              
              <p className="text-xl md:text-2xl text-vp-grey-800 max-w-4xl mx-auto leading-relaxed">
                Switching to RU ORA-7 delivers{' '}
                <span className="text-vp-green-700 font-bold">immediate and long-term return on investment (ROI)</span> by
                maximizing safety and minimizing operational costs
              </p>
            </div>

            {/* ROI Benefits */}
            <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto mb-16">
              {roiBenefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15 }}
                  whileHover={{ y: -10, scale: 1.03 }}
                  className="group relative"
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${benefit.bgColor} opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500 rounded-3xl`} />
                  
                  <div className="relative bg-white backdrop-blur-xl border-2 border-orange-200 rounded-3xl p-8 h-full group-hover:bg-orange-50 group-hover:border-orange-400 transition-all duration-500 shadow-lg">
                    <motion.div
                      className={`w-20 h-20 bg-gradient-to-br ${benefit.bgColor} rounded-2xl flex items-center justify-center mb-6`}
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.6 }}
                    >
                      <benefit.icon className="w-10 h-10 text-white" />
                    </motion.div>
                    
                    <h3 className="text-2xl md:text-3xl font-bold text-vp-grey-900 mb-4">
                      {benefit.title}
                    </h3>
                    <p className="text-vp-grey-800 leading-relaxed text-lg">
                      {benefit.desc}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Additional Safety Highlights */}
            <motion.div
              className="max-w-5xl mx-auto bg-white backdrop-blur-xl border-2 border-orange-200 rounded-3xl p-10 mb-16 shadow-xl"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              <h3 className="text-3xl font-black text-center mb-8 text-slate-900">
                Key Safety Features
              </h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                {[
                  { title: 'Fire Control', desc: 'Acts as a foaming agent to help control fires', icon: BsFire },
                  { title: 'Oil Leak Detection', desc: 'Traces oil leakage for preventive maintenance', icon: BsDroplet },
                  { title: 'Offshore Safe', desc: 'Ideal for offshore oil drilling operations', icon: FiShield },
                  { title: 'Mine Certified', desc: 'Safe for use in underground mines', icon: FiCheckCircle },
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start space-x-4 bg-orange-50 backdrop-blur-sm p-6 rounded-2xl border-2 border-orange-200"
                  >
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-600 to-red-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h4>
                      <p className="text-slate-900">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* CTA */}
            <motion.div
              className="text-center"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <Link
                to="/contact"
                className="group inline-flex items-center space-x-3 px-10 py-5 bg-white text-orange-900 font-bold text-lg rounded-full hover:bg-orange-100 transform hover:scale-105 transition-all duration-300 shadow-2xl"
              >
                <FiTool className="text-2xl" />
                <span>Request a Technical Consultation</span>
                <FiArrowRight className="group-hover:translate-x-2 transition-transform" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 md:py-32 bg-gradient-to-r from-orange-600 via-red-600 to-orange-600 relative overflow-hidden">
        {/* Particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(30)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-white rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -100, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 3,
              }}
            />
          ))}
        </div>

        <div className="relative container mx-auto px-4 lg:px-8">
          <motion.div
            className="max-w-5xl mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-8">
              Transform Your Industrial Maintenance Today
            </h2>
            
            <p className="text-xl md:text-2xl text-white mb-12 max-w-3xl mx-auto">
              Replace hazardous fossil fuels with RU ORA-7's 3-in-1 eco-safe solution
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <Link
                to="/contact"
                className="group w-full sm:w-auto px-12 py-6 bg-white text-orange-600 font-bold text-xl rounded-2xl hover:bg-slate-100 transform hover:-translate-y-2 hover:scale-105 transition-all duration-300 shadow-2xl flex items-center justify-center space-x-3"
              >
                <span>Get a Free Consultation</span>
                <FiArrowRight className="group-hover:translate-x-2 transition-transform" />
              </Link>
              
              <button
                onClick={() => handleDownloadClick('datasheet')}
                className="group w-full sm:w-auto px-12 py-6 bg-transparent border-3 border-white text-white font-bold text-xl rounded-2xl hover:bg-white hover:text-orange-600 transform hover:-translate-y-2 hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-3"
              >
                <FiDownload className="text-2xl" />
                <span>Download Technical Data Sheet</span>
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Inquiry Modal */}
      <InquiryModal 
        isOpen={modalOpen} 
        onClose={() => setModalOpen(false)}
        downloadType={downloadType}
        productName="RU ORA-7"
        downloadUrl={ruOra7Brochure}
      />
      
      {/* Contact Form Modal */}
      <ContactFormModal 
        isOpen={contactModalOpen} 
        onClose={() => setContactModalOpen(false)}
        productName="RU ORA-7"
      />
    </div>
  );
};

export default ProductRUORA7;



